
import { motion } from "framer-motion";
import { MessageCircle, ThumbsUp } from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { useState, useEffect } from "react";
import { useToast } from "./ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import LeadForm from "./lead-form/LeadForm";

interface SearchResultBoxProps {
  result: string;
  chatId?: string;
  onLike?: () => void;
}

const SearchResultBox = ({ result, chatId, onLike }: SearchResultBoxProps) => {
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (chatId) {
      fetchLikeStatus();
    }
  }, [chatId]);

  const fetchLikeStatus = async () => {
    if (!chatId) return;
    
    const { data: likeData } = await supabase
      .from('ai_response_likes')
      .select('id')
      .eq('chat_history_id', chatId)
      .single();
    
    setIsLiked(!!likeData);

    const { data: chatData } = await supabase
      .from('ai_chat_history')
      .select('likes_count')
      .eq('id', chatId)
      .single();
    
    setLikesCount(chatData?.likes_count || 0);
  };

  const handleLike = async () => {
    if (!chatId) return;

    try {
      if (!isLiked) {
        await supabase.from('ai_response_likes').insert({
          chat_history_id: chatId
        });

        await supabase
          .from('ai_chat_history')
          .update({ likes_count: likesCount + 1 })
          .eq('id', chatId);
      } else {
        await supabase
          .from('ai_response_likes')
          .delete()
          .eq('chat_history_id', chatId);

        await supabase
          .from('ai_chat_history')
          .update({ likes_count: Math.max(0, likesCount - 1) })
          .eq('id', chatId);
      }

      setIsLiked(!isLiked);
      setLikesCount(prevCount => isLiked ? prevCount - 1 : prevCount + 1);
      
      if (onLike) {
        onLike();
      }

      toast({
        title: isLiked ? "Removed like" : "Added like",
        description: isLiked ? "Your like has been removed" : "Thanks for your feedback!",
      });
    } catch (error) {
      console.error('Error handling like:', error);
      toast({
        title: "Error",
        description: "Failed to update like status. Please try again.",
        variant: "destructive",
      });
    }
  };

  const formatResult = (text: string) => {
    return text
      .split('Next Steps:')[0]
      .trim()
      .replace(/^\s*[-•*]\s*/gm, '');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-2xl mx-auto mt-8 mb-12"
    >
      <Card className="p-6 bg-white/10 backdrop-blur-md border-white/20">
        <div className="flex items-start space-x-4">
          <MessageCircle className="w-6 h-6 text-purple-400 mt-1 flex-shrink-0" />
          <div className="flex-1">
            <div className="prose prose-invert max-w-none">
              <p className="text-white/90 leading-relaxed whitespace-pre-line">
                {formatResult(result)}
              </p>
            </div>
            
            {showLeadForm && (
              <LeadForm onSubmitSuccess={() => setShowLeadForm(false)} />
            )}

            <div className="mt-4 flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowLeadForm(!showLeadForm)}
                className="text-white hover:text-white/90 hover:bg-white/10"
              >
                {showLeadForm ? 'Hide Form' : '✨ Get Started'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLike}
                className={`text-white/60 hover:text-white hover:bg-white/10 ${isLiked ? 'text-purple-400' : ''}`}
              >
                <ThumbsUp className={`w-4 h-4 mr-2 ${isLiked ? 'fill-current' : ''}`} />
                {likesCount > 0 && <span className="mr-2">{likesCount}</span>}
                {isLiked ? 'Liked' : 'Like'}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export default SearchResultBox;
